import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import agentApiService from '../../../services/agentApiService';
import DocumentUploadField from '../../common/DocumentUploadField';
import DocumentViewerModal from '../../common/DocumentViewerModal';

const ProfileCompletionModal = ({ isOpen, onClose, onProfileCompleted }) => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [formData, setFormData] = useState({
    gstNumber: '',
    gstImage: '',
    udyamAadharRegNo: '',
    udyamAadharImage: ''
  });
  const [previewModal, setPreviewModal] = useState({ isOpen: false, imageUrl: '', title: '' });

  useEffect(() => {
    if (isOpen) {
      fetchProfileData();
    }
  }, [isOpen]);

  const fetchProfileData = async () => {
    try {
      setLoading(true);
      const response = await agentApiService.getAgentProfileForCompletion();
      
      if (response.success) {
        setProfileData(response.profile);
        // Pre-fill form with existing data
        setFormData({
          gstNumber: response.profile.gstNumber || '',
          gstImage: response.profile.gstImage || '',
          udyamAadharRegNo: response.profile.udyamAadharRegNo || '',
          udyamAadharImage: response.profile.udyamAadharImage || ''
        });
        console.log('✅ Profile data loaded:', response.profile);
      } else {
        toast.error(response.message || 'Failed to load profile data');
      }
    } catch (error) {
      console.error('❌ Error fetching profile:', error);
      toast.error('Failed to load profile data');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value.toUpperCase()
    }));
  };

  const validateForm = () => {
    const hasGstNum = Boolean(formData.gstNumber?.trim());
    const hasGstImg = Boolean(formData.gstImage?.trim());
    const hasUdyamNum = Boolean(formData.udyamAadharRegNo?.trim());
    const hasUdyamImg = Boolean(formData.udyamAadharImage?.trim());

    const hasExistingGst = Boolean(profileData?.gstNumber?.trim() && profileData?.gstNumber !== 'N/A' && profileData?.gstImage?.trim());
    const hasExistingUdyam = Boolean(profileData?.udyamAadharRegNo?.trim() && profileData?.udyamAadharRegNo !== 'N/A' && profileData?.udyamAadharImage?.trim());

    // If GST number is entered, GST image is mandatory
    if (hasGstNum && !hasGstImg) {
      toast.error('Please upload the GST Certificate image for the entered GST Number');
      return false;
    }
    // If GST image is uploaded, GST number is mandatory
    if (hasGstImg && !hasGstNum) {
      toast.error('Please enter the GST Number for the uploaded GST Certificate');
      return false;
    }

    // If Udyam number is entered, Udyam image is mandatory
    if (hasUdyamNum && !hasUdyamImg) {
      toast.error('Please upload the Udyam Aadhaar document image for the entered Udyam Number');
      return false;
    }
    // If Udyam image is uploaded, Udyam number is mandatory
    if (hasUdyamImg && !hasUdyamNum) {
      toast.error('Please enter the Udyam Aadhaar Registration Number for the uploaded document');
      return false;
    }

    const isGstComplete = hasGstNum && hasGstImg;
    const isUdyamComplete = hasUdyamNum && hasUdyamImg;

    // At least one of GST or Udyam Aadhaar (Number + Image) must be provided (or already in DB)
    if (!isGstComplete && !isUdyamComplete && !hasExistingGst && !hasExistingUdyam) {
      toast.error('Please provide both the number and certificate image for either GST or Udyam Aadhaar');
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setSubmitting(true);
      
      const response = await agentApiService.updateAgentProfile(
        profileData.agentId,
        formData
      );
      
      if (response.success) {
        toast.success('Documents submitted for verification successfully!');
        console.log('✅ Profile updated successfully');
        
        // Close modal and notify parent
        onClose();
        if (onProfileCompleted) {
          onProfileCompleted();
        }
      } else {
        toast.error(response.message || 'Failed to update profile');
      }
    } catch (error) {
      console.error('❌ Error updating profile:', error);
      toast.error('Failed to update profile');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={onClose}
        ></div>

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-6">
            {/* Header */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0 p-2 bg-yellow-100 rounded-lg">
                    <svg className="w-8 h-8 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h4 className="text-xl font-bold text-gray-900">Complete Your Profile</h4>
                    <p className="text-sm text-gray-600">
                      Please provide either GST or Udyam Aadhaar details along with the document image to proceed with booking
                    </p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-600 transition-colors p-1.5 rounded-lg hover:bg-gray-100"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                {/* Rejection Alert if previous submission was rejected */}
                {profileData?.documentVerificationStatus === 'REJECTED' && (
                  <div className="mb-5 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start space-x-3">
                    <i className="fas fa-exclamation-circle text-red-600 mt-0.5 text-lg flex-shrink-0"></i>
                    <div>
                      <h5 className="text-sm font-bold text-red-900">Previous Submission Rejected</h5>
                      <p className="text-xs text-red-700 mt-0.5">
                        {profileData.documentRejectionReason || 'Your previous document was rejected by our verification team. Please provide clear and valid documents.'}
                      </p>
                    </div>
                  </div>
                )}

                {/* Under Verification Banner if already submitted */}
                {profileData?.documentVerificationStatus === 'PENDING_APPROVAL' && (
                  <div className="mb-5 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-start space-x-3">
                    <i className="fas fa-clock text-amber-600 mt-0.5 text-lg flex-shrink-0"></i>
                    <div>
                      <h5 className="text-sm font-bold text-amber-900">Documents Under Review</h5>
                      <p className="text-xs text-amber-700 mt-0.5">
                        Your submitted documents are currently being verified by our Admin/Support team. You can re-upload here if you need to update them.
                      </p>
                    </div>
                  </div>
                )}

                {/* Current Profile Info */}
                {profileData && (
                  <div className="mb-5 bg-gray-50 border border-gray-200 rounded-lg p-3.5">
                    <h5 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Your Current Information</h5>
                    <div className="grid grid-cols-2 gap-2.5 text-xs">
                      <div>
                        <span className="text-gray-500">Name:</span>
                        <span className="ml-1.5 font-semibold text-gray-800">{profileData.firstName} {profileData.lastName}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Email:</span>
                        <span className="ml-1.5 font-semibold text-gray-800">{profileData.email}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Phone:</span>
                        <span className="ml-1.5 font-semibold text-gray-800">{profileData.phoneNumber}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Business:</span>
                        <span className="ml-1.5 font-semibold text-gray-800">{profileData.businessName || 'N/A'}</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Form Fields: GST & Udyam Aadhar with Side-by-side Image Upload */}
                <div className="space-y-4">
                  {/* GST Number & Certificate Image Upload */}
                  <DocumentUploadField
                    label="GST Number"
                    name="gstNumber"
                    value={formData.gstNumber}
                    onChange={handleInputChange}
                    imageValue={formData.gstImage}
                    onImageChange={(img) => setFormData((prev) => ({ ...prev, gstImage: img }))}
                    required={false}
                    imageRequired={Boolean(formData.gstNumber && formData.gstNumber.trim() !== '')}
                    placeholder="e.g. 22AAAAA0000A1Z5"
                    documentTitle="GST Certificate"
                    helpText="If GST is provided, both number and certificate photo are required"
                    onPreviewImage={(url, title) => setPreviewModal({ isOpen: true, imageUrl: url, title })}
                  />

                  {/* Udyam Aadhar Registration Number & Document Image Upload */}
                  <DocumentUploadField
                    label="Udyam Aadhar Registration Number"
                    name="udyamAadharRegNo"
                    value={formData.udyamAadharRegNo}
                    onChange={handleInputChange}
                    imageValue={formData.udyamAadharImage}
                    onImageChange={(img) => setFormData((prev) => ({ ...prev, udyamAadharImage: img }))}
                    required={false}
                    imageRequired={Boolean(formData.udyamAadharRegNo && formData.udyamAadharRegNo.trim() !== '')}
                    placeholder="e.g. 123456789012 or UDYAM-XX-00-0000000"
                    documentTitle="Udyam Aadhaar"
                    helpText="If Udyam Aadhaar is provided, both number and document photo are required"
                    onPreviewImage={(url, title) => setPreviewModal({ isOpen: true, imageUrl: url, title })}
                  />
                </div>

                {/* Info Box */}
                <div className="mt-5 bg-blue-50 border border-blue-200 rounded-lg p-3.5">
                  <div className="flex items-start">
                    <svg className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    <div className="ml-3">
                      <h5 className="text-xs font-bold text-blue-900 uppercase tracking-wide">Verification & Compliance</h5>
                      <p className="mt-0.5 text-xs text-blue-800">
                        At least one business document (GST or Udyam Aadhaar) is required. If you enter GST or Udyam, attaching the document image is mandatory for verification.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="mt-6 flex space-x-3">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="flex-1 bg-blue-600 text-white py-3 px-4 rounded-lg font-bold hover:bg-blue-700 transition-colors disabled:bg-blue-400 disabled:cursor-not-allowed flex items-center justify-center shadow-sm"
                  >
                    {submitting ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Submitting...
                      </>
                    ) : (
                      'Submit & Continue'
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={onClose}
                    disabled={submitting}
                    className="flex-1 bg-gray-200 text-gray-800 py-3 px-4 rounded-lg font-medium hover:bg-gray-300 transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>

        {/* Document Full Image Preview Modal */}
        <DocumentViewerModal
          isOpen={previewModal.isOpen}
          onClose={() => setPreviewModal({ isOpen: false, imageUrl: '', title: '' })}
          imageUrl={previewModal.imageUrl}
          title={previewModal.title}
        />
      </div>
    </div>
  );
};

export default ProfileCompletionModal;
